import { Injectable } from '@angular/core';

import { WireBlockTypeApiEnum } from '../../Wire/enums/wire-block-type.enum';
import { linkTypeEnumToWireSocialLinkType, WireSocialLinkEnum } from '../../Wire/enums/wire-social-link.enum';
import { SocialLinkDisplayElementApiModel } from '../../Wire/models/social-link-display-element-api.model';
import { WireBlockApiModel } from '../../Wire/models/wire-block-api.model';
import { WireImageDisplayElementApiModel } from '../../Wire/models/wire-image-display-element-api.model';
import { WireSocialLinkModel } from '../../Wire/models/wire-social-link.model';
import { WireTextDisplayElementApiModel } from '../../Wire/models/wire-text-display-element-api.model';
import { DistributionBlockTypeApiEnum } from '../../../api/distributions/enum/distribution-block-type-api.enum';
import { DistributionLinkDisplayElementApiModel } from '../../../api/distributions/model/distribution-link-display-element-api.model';
import { DistributionBlockApiModel } from '../../../api/distributions/model/distribution-block-api.model';
import { DistributionButtonDisplayElementApiModel } from '../../../api/distributions/model/distribution-button-display-element-api.model';
import { DistributionImageDisplayElementApiModel } from '../../../api/distributions/model/distribution-image-display-element-api.model';
import { DistributionTextDisplayElementApiModel } from '../../../api/distributions/model/distribution-text-display-element-api.model';
import { TypeConverter } from '../../../Core/utils';
import { HandleHelper } from '../../../Common/Forms/helpers/handle.helper';
import { FontAlignmentEnum } from '../../../Common/Forms/enums/font-alignment.enum';
import { SocialLinkStyleModel } from '../../../Common/PageBuilder/models/link-style.model';
import { ButtonStyleModel } from '../../../Common/PageBuilder/models/button-style.model';
import { DistributionPlainTextService } from '../../../Discover/Targeting/components/Distribution/PlainTextEditorDialog/plain-text.service';

@Injectable()
export class DistributionBlockApiModelToWireBlockApiModelConverter implements TypeConverter<DistributionBlockApiModel, WireBlockApiModel> {
    private static MAIL_TO_PREFIX = 'mailto:';

    constructor(
        private plainTextService: DistributionPlainTextService
    ) {
    }

    convert(distributionBlock: DistributionBlockApiModel): WireBlockApiModel {
        switch (distributionBlock.type) {
            case DistributionBlockTypeApiEnum.text:
                return this.convertTextBlock(distributionBlock as DistributionTextDisplayElementApiModel);
            case DistributionBlockTypeApiEnum.button:
                return this.convertButtonBlock(distributionBlock as DistributionButtonDisplayElementApiModel);
            case DistributionBlockTypeApiEnum.image:
                return this.convertImageBlock(distributionBlock as DistributionImageDisplayElementApiModel);
            case DistributionBlockTypeApiEnum.link:
                return this.convertLinkBlock(distributionBlock as DistributionLinkDisplayElementApiModel);
            default:
                return null;
        }
    }

    private convertTextBlock(textBlock: DistributionTextDisplayElementApiModel): WireTextDisplayElementApiModel {
        const convertedText = this.plainTextService.parseHTMLWithoutMarkdown(textBlock.text);
        return {
            ...textBlock,
            text: convertedText,
            // Wire text blocks have no styling
            styles: '{}',
            type: WireBlockTypeApiEnum.text
        };
    }

    private convertButtonBlock(buttonBlock: DistributionButtonDisplayElementApiModel): SocialLinkDisplayElementApiModel {
        if (!buttonBlock.url || buttonBlock.url.startsWith(DistributionBlockApiModelToWireBlockApiModelConverter.MAIL_TO_PREFIX)) {
            return null;
        }

        let linkType = WireSocialLinkEnum.WEBSITE;

        if (HandleHelper.FACEBOOK_PAGE_FORMAT.test(buttonBlock.url)) {
            linkType = WireSocialLinkEnum.FACEBOOK;
        } else if (HandleHelper.TWITTER_PAGE_FORMAT.test(buttonBlock.url)) {
            linkType = WireSocialLinkEnum.TWITTER;
        } else if (HandleHelper.LINKEDIN_PAGE_FORMAT.test(buttonBlock.url) || HandleHelper.LINKEDIN_COMPANY_PAGE_FORMAT.test(buttonBlock.url)) {
            linkType = WireSocialLinkEnum.LINKEDIN;
        } else if (HandleHelper.INSTAGRAM_PAGE_FORMAT.test(buttonBlock.url)) {
            linkType = WireSocialLinkEnum.INSTAGRAM;
        } else if (HandleHelper.YOUTUBE_CHANNEL_FORMAT.test(buttonBlock.url)) {
            linkType = WireSocialLinkEnum.YOUTUBE;
        } else if (HandleHelper.TIKTOK_USER_FORMAT.test(buttonBlock.url)) {
            linkType = WireSocialLinkEnum.TIKTOK;
        } else if (HandleHelper.SNAPCHAT_PAGE_FORMAT.test(buttonBlock.url)) {
            linkType = WireSocialLinkEnum.SNAPCHAT;
        }

        const link = new WireSocialLinkModel({
            linkType: linkType,
            url: buttonBlock.url,
            text: buttonBlock.text
        } as WireSocialLinkModel);

        const buttonStyles = new ButtonStyleModel(buttonBlock.styles);
        const mappedStyles = new SocialLinkStyleModel();
        // Hack: Types are equivalent but TS doesn't like it
        mappedStyles.alignment = buttonStyles.buttonAlignment as unknown as FontAlignmentEnum;

        for (const prop in mappedStyles) {
            if (mappedStyles.hasOwnProperty(prop) && buttonStyles.hasOwnProperty(prop)) {
                mappedStyles[prop] = buttonStyles[prop];
            }
        }

        delete mappedStyles.styleState;

        return {
            id: buttonBlock.id,
            type: WireBlockTypeApiEnum.links,
            styles: JSON.stringify(mappedStyles.toApi()),
            customStyleId: buttonBlock.customStyleId,
            socialLinks: [ link ]
        } as SocialLinkDisplayElementApiModel;
    }

    private convertImageBlock(imageBlock: DistributionImageDisplayElementApiModel): WireImageDisplayElementApiModel {
        return {
            ...imageBlock,
            type: WireBlockTypeApiEnum.image,
        } as WireImageDisplayElementApiModel;
    }

    private convertLinkBlock(linkBlock: DistributionLinkDisplayElementApiModel): SocialLinkDisplayElementApiModel {
        const links = linkBlock.links?.map(link => new WireSocialLinkModel(({
            linkType: linkTypeEnumToWireSocialLinkType[link.type],
            url: link.url,
            text: link.text
        } as WireSocialLinkModel))) ?? [];

        const distLinkStyles = new SocialLinkStyleModel(linkBlock.styles);
        const mappedStyles = new SocialLinkStyleModel();

        mappedStyles.alignment = distLinkStyles.alignment;
        mappedStyles.fontSize = distLinkStyles.fontSize;
        mappedStyles.underlined = distLinkStyles.underlined;
        mappedStyles.iconColor = distLinkStyles.iconColor;
        mappedStyles.iconShape =  distLinkStyles.iconShape;
        mappedStyles.iconSize = distLinkStyles.iconSize;
        mappedStyles.iconBackgroundColor = distLinkStyles.iconBackgroundColor;
        mappedStyles.displayOption = distLinkStyles.displayOption;
        mappedStyles.layoutOption = distLinkStyles.layoutOption;

        delete mappedStyles.styleState;

        return {
            ...linkBlock,
            socialLinks: links,
            styles: JSON.stringify(mappedStyles.toApi()),
            type: WireBlockTypeApiEnum.links,
        } as SocialLinkDisplayElementApiModel;
    }
}
